import { useState, useEffect, useCallback, useMemo } from "react";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { useSelector } from "react-redux";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  ReMixChart,
  lightenColor,
} from "../../../components/recharts/ReMixCharts";
import { RajasthanDiscomData, RajGenProfile } from "../../../models/rajasthan";
import {
  getRajasthanForecastDiscomData,
  getRajGenProfileData,
  getSurDefProfile,
} from "../Utility/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../const";
import { FUNCTIONS } from "../const_raj";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "./SecondaryHeader";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import Tooltip from "../../../components/tooltip";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { BrushStart } from "../../../models/chart_model";
import RajasthanForecastPeakAnalysis from "./Forecast_SurplusPeak";
import RajasthanForecastSummaryAnalysis from "./Forecast_SurplusSummary";
import { getRajasthanSurplusMetrics } from "../Utility/RestApi";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { SurplusDeficitData } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
// Grouping and filtering configurations
const GROUP_BY = [
  {
    label: "Area",
    key: "area",
    values: ["area", "discom"],
  },
  {
    label: "Discom",
    key: "discom",
    values: ["discom"],
  },
];

const DISCOM = [
  {
    label: "JDVVNL",
    key: "JDVVNL",
  },
  {
    label: "JVVNL",
    key: "JVVNL",
  },
  {
    label: "AVVNL",
    key: "AVVNL",
  },
];

// Updated GEN_BY using only payload keys and computing deficit from forecasted and total
const GEN_BY = [
  {
    label: "Cumulative",
    keys: [
      {
        key: "deficit",
        label: "Surplus/Deficit",
        stackId: "deficit",
      },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
      {
        key: "total",
        label: "Total Gen. + Proc.",
        stackId: "gen",
      },
    ],
  },
  {
    label: "Comparative",
    keys: [
      {
        key: "deficit",
        label: "Surplus/Deficit",
        stackId: "deficit",
      },
      {
        key: "deficit_percent",
        label: "Surplus/Deficit %",
        yAxis: "right",
        stackId: "deficit",
      },
      { key: "sgu", label: "SGU", stackId: "gen" },
      { key: "biomass", label: "Biomass", stackId: "gen" },
      { key: "hydro", label: "Hydro", stackId: "gen" },
      { key: "solar", label: "Solar", stackId: "gen" },
      { key: "wind", label: "Wind", stackId: "gen" },
      { key: "isgs", label: "ISGS", stackId: "gen" },
      { key: "lta", label: "LTA", stackId: "gen" },
      { key: "ldc", label: "LDC", stackId: "gen" },
      {
        key: "kalisindhtpsstage1_unit1",
        label: "kalisindhtpsstage1 unit1",
        stackId: "gen",
      },
      { key: "adanikawai", label: "Adani Kawai", stackId: "gen" },
      { key: "barsingsarneyveli", label: "Barsingsar Neyveli", stackId: "gen" },
      {
        key: "chhabratpsstage1_unit12",
        label: "Chhabra TPS Stage1 Unit1/2",
        stackId: "gen",
      },
      { key: "csctpp", label: "CSCTPP", stackId: "gen" },
      {
        key: "dholpurgtpp_unit123",
        label: "Dholpur GTPP Unit1/2/3",
        stackId: "gen",
      },
      { key: "giral", label: "Giral", stackId: "gen" },
      { key: "ktpsstage12345", label: "KTPS Stage1/2/3/4/5", stackId: "gen" },
      { key: "rajwest_unit8", label: "Rajwest Unit8", stackId: "gen" },
      {
        key: "ramgarhgtppstage123",
        label: "Ramgarh GTPP Stage1/2/3",
        stackId: "gen",
      },
      { key: "rappa", label: "RAPPA", stackId: "gen" },
      { key: "ssctps_6", label: "SSCTPS 6", stackId: "gen" },
      { key: "stpsstage1234", label: "STPS Stage1/2/3/4", stackId: "gen" },
      { key: "vslignite", label: "VS Lignite", stackId: "gen" },
    ],
  },
];

const DRAWAL_BY = [
  {
    label: "Cumulative",
    keys: [{ key: "forecasted", label: "Total Drawal", stackId: "drawal" }],
  },
  {
    label: "Comparative",
    keys: [{ key: "forecasted", label: "Total Drawal", stackId: "drawal" }],
  },
];

// X_AXIS uses time_block (which exists in payload) and date
const X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Discom/ Area",
    key: "area",
    isTime: false,
    values: ["area", "discom"],
  },
];

const Intervals = [
  { label: "Time Slot", key: "time", values: ["time_block", "date"], isTime: true },
  { label: "Date", key: "date", values: ["date"], isTime: false },
  { label: "Month", key: "month", values: ["month"], isTime: false },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const CONTENT_TYPE = ["Chart", "Table"];

const MODELS = ["Model 1", "Model 2"];

export function Rajasthan_Forecast_Surplus() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [data, setData] = useState<RajasthanDiscomData[]>([]);
  const [genData, setGenData] = useState<RajGenProfile[]>([]);
  const [genGroupBy, setGenGroupBy] = useState(GEN_BY[0]);
  const [drawalGroupBy, setDrawalGroupBy] = useState(DRAWAL_BY[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedContentType, setSelectedContentType] = useState(
    CONTENT_TYPE[0],
  );
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([GROUP_BY[1]]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<SurplusDeficitData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const metricsData = await getRajasthanSurplusMetrics({
        ranges: [
          {
            startDate: startDateStr,
            endDate: endDateStr,
            blocks: selectedBlock.map((block) => block.value),
          },
        ],
        aggregated: true,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);
  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const newData = await getRajasthanForecastDiscomData(
      convertToString(dates.startDate),
      convertToString(dates.endDate),
      selectedModel,
    );
    const genData = await getSurDefProfile({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });
    setData(newData);
    setGenData(genData);

    setUniqueDate(
      Array.from(
        new Set(
          newData
            .map((item) => item.date)
            .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
        ),
      ),
    );
    // Extract unique time_block values and sort them numerically
    const uniqueTimeBlocks = Array.from(
      new Set(newData.map((item: any) => item.time_block)),
    ).sort((a: any, b: any) => Number(a) - Number(b));
    setUniqueBlock(uniqueTimeBlocks);
    setLoading(false);
  }, [activeTab, DefaultDates, selectedModel]);

  useEffect(() => {
    // Derive month field from date
    const addMonth = (item: any) => {
      const dateParts = item.date?.split("-") || [];
      let monthStr = "";
      if (dateParts.length === 3) {
        const year = dateParts[0];
        const monthIndex = parseInt(dateParts[1]) - 1;
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
        ];
        monthStr =
          monthIndex >= 0 && monthIndex < 12
            ? `${monthNames[monthIndex]}-${year.slice(-2)}`
            : getMonthFromDate(item.date);
      } else {
        monthStr = getMonthFromDate(item.date);
      }
      return { ...item, month: monthStr };
    };

    let filteredData = data.map(addMonth);

    // Filter by discom
    if (selectedDiscom.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom),
      );
    }

    // Filter by date
    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );
    }

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item: any) =>
        selectedBlock
          .map((b: any) => (typeof b === "object" ? b?.value : b))
          .includes(item.time_block),
      );
    }

    // Filter by date range
    const dates = DefaultDates[activeTab].defaultRangeDate;
    filteredData = filteredData.filter(
      (item) =>
        new Date(item.date) >= dates.startDate &&
        new Date(item.date) <= dates.endDate,
    );

    // Determine correct keys based on interval selection
    const determineKeys = () => {
      if (selectedInterval.key === "time") return ["time_block", "date"];
      if (selectedInterval.key === "date") return ["date"];
      return [selectedInterval.values[0]];
    };

    // For the drawal/chart data
    const par: ChartDataParams = {
      keys: determineKeys(),
      groupBy: selectedXaxis.isTime ? selectedGroupKeys.map((k) => k.key) : [],
      tableKeys: ["date", "time_block", "month"],
      volumeKey: "forecasted",
      aggregationKeys: ["forecasted"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);

    // Map forecasted to Total_drawal_mw
    const drawal = cData.data.map((item) => ({
      ...item,
      Total_drawal_mw: item.forecasted,
    }));

    function cleanOnbardcKeys(obj: Record<string, any>) {
      const cleaned: Record<string, any> = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (key.startsWith("onbardc_")) {
          const newKey = key.replace(/^onbardc_/, "");
          cleaned[newKey] = value;
        } else {
          cleaned[key] = value;
        }
      });
      return cleaned;
    }

    const final_raw_data = genData.map(cleanOnbardcKeys).map(addMonth);

    // FIXED: Filter genData by selected blocks using 'block' field
    let filteredGenData = final_raw_data;

    // Filter genData by date range
    filteredGenData = filteredGenData.filter((item: any) => {
      const itemDate = new Date(item.date);
      return itemDate >= dates.startDate && itemDate <= dates.endDate;
    });

    // Filter genData by selected date
    if (selectedDate.length > 0) {
      filteredGenData = filteredGenData.filter((item: any) =>
        selectedDate.map((d) => d.value).includes(item.date),
      );
    }

    // FIXED: Filter genData by selected blocks using sanitized values
    if (selectedBlock.length > 0) {
      filteredGenData = filteredGenData.filter((item: any) =>
        selectedBlock
          .map((b: any) => (typeof b === "object" ? b?.value : b))
          .includes(item.time_block),
      );
    }

    // Update the aggregation to use only the payload keys
    const allAggKeys = [
      "forecasted",
      "total",
      "sgu",
      "biomass",
      "hydro",
      "solar",
      "wind",
      "isgs",
      "lta",
      "ldc",
      "kalisindhtpsstage1_unit1",
      "adanikawai",
      "barsingsarneyveli",
      "chhabratpsstage1_unit12",
      "csctpp",
      "dholpurgtpp_unit123",
      "giral",
      "ktpsstage12345",
      "rajwest_unit8",
      "ramgarhgtppstage123",
      "rappa",
      "ssctps_6",
      "stpsstage1234",
      "vslignite",
    ];
    const chartPar: ChartDataParams = {
      keys: determineKeys(),
      tableKeys: ["date", "time_block", "month"],
      groupBy: [],
      aggregationKeys: allAggKeys,
      averageKeys: allAggKeys,
      averageDuplicationKeys: ["date", "time_block", "month"],
      priceKey: "",
      volumeKey: "",
      data: filteredGenData,
    };

    const res_data = FormatChartData(chartPar);

    // Compute deficit (as total - forecasted) and its percent
    const final_data = res_data.data.map((item: any) => {
      const deficit = item._total - item._forecasted;
      const deficit_percent =
        item._forecasted !== 0 ? (deficit / item._forecasted) * 100 : 0;

      return {
        ...item,
        _deficit: deficit,
        _deficit_percent: deficit_percent,
      };
    });

    const finalTableData = res_data.data.map((item: any) => {
      const deficit = item._total - item._forecasted;
      const deficit_percent =
        item._forecasted !== 0 ? (deficit / item._forecasted) * 100 : 0;

      return {
        ...item,
        _deficit: deficit,
        _deficit_percent: deficit_percent,
      };
    });

    // FIXED: Sort the chart data properly by date first, then by time_block
    const sortedChartData = final_data.sort((a, b) => {
      // First sort by date
      if (a.date && b.date) {
        const dateComparison =
          new Date(a.date).getTime() - new Date(b.date).getTime();
        if (dateComparison !== 0) return dateComparison;
      }
      // Then sort by time_block numerically
      if (a.time_block !== undefined && b.time_block !== undefined) {
        return Number(a.time_block) - Number(b.time_block);
      }
      return 0;
    });

    setChartData(sortedChartData);
    setChartKeys(cData.keys);
    setTableData(finalTableData || []);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedInterval,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedChartType,
    selectedContentType,
    genData,
    DefaultDates,
    activeTab,
  ]);

  // Determine xDataKey based on interval selection
  const getXDataKey = () => {
    if (selectedInterval.key === "time") return "time_block";
    return selectedInterval.key;
  };

  // Determine secondXDataKey - show date when in time mode
  const getSecondXDataKey = () => {
    return selectedInterval.isTime ? "date" : undefined;
  };

  const allLegends = useMemo(
    () =>
      drawalGroupBy.keys
        .concat(genGroupBy.keys)
        .reverse()
        .map((key) => ({
          dataKey: "_" + key.key,
          name: key.label,
        })),
    [drawalGroupBy.keys, genGroupBy.keys],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(allLegends, [chartData]);

  const tablePar = {
    legends: visibleLegends,
    tableData: TableData,
    filename: AddDatesWithFileName({
      fileName: "Forecast Discom Load",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
    data: chartData,
    unit: "MW",
    extraKeys: ["time_block", "date"],
    extraLabels: ["Block", "Date"],
  };

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleModelChange(model: string) {
    setSelectedModel(model);
  }

  function handleDiscomChange(items: string[]) {
    setSelectedDiscom(items);
  }

  function handleGenGroupByChange(index: number) {
    setGenGroupBy(GEN_BY[index]);
  }

  // Show Peak Analysis component when selected
  if (selectedXAxisMode.value === "peak") {
    return (
      <RajasthanForecastPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        genGroupBy={genGroupBy}
        onGenGroupByChange={handleGenGroupByChange}
        metrics={metrics}
      />
    );
  }

  // Show Summary component when selected
  if (selectedXAxisMode.value === "summary") {
    return (
      <RajasthanForecastSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedDiscom={selectedDiscom}
        onDiscomChange={handleDiscomChange}
        genGroupBy={genGroupBy}
        onGenGroupByChange={handleGenGroupByChange}
        metrics={metrics}
      />
    );
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          {/* <FilterMenu
            title="Model"
            items={MODELS}
            selectedItems={[selectedModel]}
            onSelect={(item, index) => {
              handleModelChange(item);
            }}
          /> */}
          <FilterMenu
            title="Interval"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
            }}
          />
          <FilterMenu
            title="Generation By"
            items={GEN_BY.map((item) => item.label)}
            selectedItems={[genGroupBy.label]}
            onSelect={(item, index) => {
              setGenGroupBy(GEN_BY[index]);
            }}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
         <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Surplus/Deficit"
              items={[
                {
                  label: "Maximum",
                  value:
                    metrics?.surplus_deficit_metrics?.[
                      "Max Surplus/Deficit (MW)"
                    ]?.value ?? undefined,
                  unit:
                    metrics?.surplus_deficit_metrics?.[
                      "Max Surplus/Deficit (MW)"
                    ]?.unit ?? "",
                },
                {
                  label: "Minimum",
                  value:
                    metrics?.surplus_deficit_metrics?.[
                      "Min Surplus/Deficit (MW)"
                    ]?.value ?? undefined,
                  unit:
                    metrics?.surplus_deficit_metrics?.[
                      "Min Surplus/Deficit (MW)"
                    ]?.unit ?? "",
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Total Generation"
              items={[
                {
                  label: "Maximum",
                  value:
                    metrics?.generation_metrics?.["Max Generation (MW)"]
                      ?.value ?? undefined,
                  unit:
                    metrics?.generation_metrics?.["Max Generation (MW)"]
                      ?.unit ?? "",
                },
                {
                  label: "Minimum",
                  value:
                    metrics?.generation_metrics?.["Min Generation (MW)"]
                      ?.value ?? undefined,
                  unit:
                    metrics?.generation_metrics?.["Min Generation (MW)"]
                      ?.unit ?? "",
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Load"
              items={[
                {
                  label: "Maximum",
                  value:
                    metrics?.load_metrics?.["Max Load (MW)"]?.value ??
                    undefined,
                  unit: metrics?.load_metrics?.["Max Load (MW)"]?.unit ?? "",
                },
                {
                  label: "Minimum",
                  value:
                    metrics?.load_metrics?.["Min Load (MW)"]?.value ??
                    undefined,
                  unit: metrics?.load_metrics?.["Min Load (MW)"]?.unit ?? "",
                },
              ]}
            />
          </div>
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading Surplus/Deficit" subtitle="Fetching generation vs load surplus & deficit data…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading">Surplus/Deficit Analysis (MW)</h2>
            <div className="flex gap-x-6  justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Search Block"
                selectedOptions={selectedBlock.map((item: any) =>
                  typeof item === "object" ? item?.value : item,
                )}
                setSelectedOptions={setSelectedBlock}
              />
              <button
                onClick={() => {
                  if (dataTab.label === "Chart") {
                    downloadChartTableCSV(tablePar);
                  }
                  if (dataTab.label === "Summary") {
                    downloadSummaryCSV({ ...tablePar });
                  }
                  if (dataTab.label === "Table") {
                    downloadChartTableCSV(tablePar);
                  }
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
         
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedInterval.isTime}
              onlyTitle={selectedBlock.length > 0}
              barCategoryGap={10}
              unit="MW"
              yAxisLabel="MW"
              secondYAxisLabel="%"
              legends={allLegends.map((leg) => ({
                ...leg,
                legendColor: undefined,
                type: selectedChartType.value,
                yAxisId: undefined,
                stackId: selectedChartType.stackId ?? undefined,
              }))}
              onLegendSelect={handleLegendSelect}
              defaultSelectedLegends={selectedLegends}
              xDataKey={getXDataKey()}
              secondXDataKey={getSecondXDataKey()}
              xTick={
                selectedInterval.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => renderHourTick(value, 10)
                  : undefined
              }
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>Forecast of Total Drawal</p>}
              text="1. Total Drawal : Forecasted Total Load (T+1)."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
